import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import classNames from 'classnames';
import {
  OverviewPerformanceTable,
  ISortState,
} from '@bingads-webui-campaign-react/overview-performance-table';
import { useHeaderStateContext } from '@bingads-webui-campaign-react/overview-card';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { getRoutePathById, allSiteMap } from '@bingads-webui-react/with-site-map';

import { IOverviewTableProps, IScopeContext } from './types';
import { useCombinedCallData } from './use-combined-call-data';
import { getOrderFromGridSortForCombinedCall } from './order-by-helper';
import { defaultComparisonMetric } from './consts';
import { useStyles } from './overview-grid-card-style';

export function OverviewAccountTable({
  metricFormatter,
  i18n,
  constructOptions,
  dateRange,
  filter,
  permissions,
  jsdata,
  dateUtil,
  getCardsConfig,
  allMetrics,
  initialSelectedMetrics,
  initialSortColumn,
  initialSortDirection,
  patchPreference,
  perfMarkerUtils,
  campaignType,
}: IOverviewTableProps) {
  const { scope } = useContext(ScopeContext) as IScopeContext;
  const { cid, uid } = scope?.values;
  const [selectedMetrics, setSelectedMetrics] = useState(initialSelectedMetrics);
  const [sortState, setSortState] = useState<ISortState>({ sortColumn: initialSortColumn, sortDirection: initialSortDirection });
  const headerState = useHeaderStateContext();
  const comparisonMajorMetric = dateRange.compared ? headerState?.selectedItemValue : defaultComparisonMetric;
  const [isFirstDataLoaded, setIsFirstDataLoaded] = useState(false);
  const classes = useStyles();
  const {
    willFetchData,
    willRender,
    done,
  } = perfMarkerUtils;

  const {
    sortColumn,
    sortDirection,
  } = useMemo(() => getOrderFromGridSortForCombinedCall({
    sortColumn: sortState.sortColumn,
    sortDirection: sortState.sortDirection,
    dateRange,
    comparisonMajorMetric,
  }), [comparisonMajorMetric, dateRange, sortState]);

  const {
    data,
    isLoading,
  } = useCombinedCallData({
    dateRange,
    filter,
    constructOptions,
    permissions,
    jsdata,
    dateUtil,
    getCardsConfig,
    sortColumn,
    sortDirection,
    willFetchData,
    willRender,
    campaignType,
  });

  useEffect(() => {
    if (!isFirstDataLoaded && !isLoading) {
      setIsFirstDataLoaded(true);
      done();
    }
  }, [data, done, isFirstDataLoaded, isLoading, willRender]);

  const onChange = useCallback(({
    newSelectedMetrics,
    newSortState,
  }) => {
    if (newSelectedMetrics) {
      setSelectedMetrics(newSelectedMetrics);
      const newSelectedColumns = newSelectedMetrics.map(m => m.key);
      patchPreference({
        selectedColumns: newSelectedColumns,
      });
    }
    if (newSortState) {
      setSortState(newSortState);
      patchPreference(newSortState);
    }
  }, [patchPreference]);

  const onFooterButtonClick = useCallback(() => {
    scope.save(getRoutePathById(allSiteMap, 'performance'));
  }, [scope]);

  const getHref = useCallback((entityId: string) => {
    const route = getRoutePathById(allSiteMap, 'overview');
    const search = `cid=${cid}&uid=${uid}&aid=${entityId}`;
    return `${route}?${search}`;
  }, [cid, uid]);

  return (
    <div className={classNames(classes.gridStyle, 'ba-account-overview-table')}>
      <OverviewPerformanceTable
        isLoading={isLoading}
        entityDisPlayName={i18n.getString(_TL_('Account'))}
        selectedMetrics={selectedMetrics}
        allMetrics={allMetrics}
        sortState={sortState}
        data={data}
        onChange={onChange}
        i18n={i18n}
        metricFormatter={metricFormatter}
        comparisonMajorMetric={comparisonMajorMetric || defaultComparisonMetric}
        footerButtonConfigs={[
          {
          text: i18n.getString(_TL_('View all accounts')),
          onClick: onFooterButtonClick,
          className: 'view-all-accounts',
          },
        ]}
        getHref={getHref}
        entityType="Account"
      />
    </div>
  );
}

